import { Box, Container, Grid, InputAdornment, TextField, Toolbar, Typography, useMediaQuery } from '@mui/material';

import BackLink from "../../Components/BackLink";
import ButtonBlock from "../../Components/Block/ButtonBlock";
import FilterListIcon from '@mui/icons-material/FilterList';
import NotesDialog from "../../Components/Dialog/NotesDialog";
import OutlinedButton from "../../Components/OutlinedButton";
import { useEffect, useMemo, useRef, useState } from "react";
import SearchIcon from '@mui/icons-material/Search';
import SingleSelectMenu from "../../Components/Menu/SingleSelectMenu";
import { getEntryNotes } from '../../Utils/Api';
import makeStyles from '@mui/styles/makeStyles';
import theme from "../../theme.js";
import Header from '../../Layouts/Header';
import Page from '../../Components/Page';
import EscapeListener from '../../Components/Utils/EscapeListener';
import TabAwayListener from '../../Components/Utils/TabAwayListener';
import { useScrollToTop } from '../../Utils/ScrollToTop';
import Nl2br from '../../Components/Utils/Nl2br';

const useStyles = makeStyles((theme) => ({
    header: {
        backgroundColor: '#121212', //theme.palette.primary.main,
        color: theme.palette.secondary.light,
    },
    gridWrap: {
        paddingLeft: theme.spacing(0),
        paddingRight: theme.spacing(0),
        [theme.breakpoints.up('sm')]: {
            paddingLeft: theme.spacing(5),
            paddingRight: theme.spacing(5),
        },
    },
    titleContainer: {
        textAlign: "center",
    },
    title: {
        textTransform: "uppercase",
        //fontSize: "1.2rem",
    },
    contentHeaderWrap: {
        paddingTop: theme.spacing(1.5),
        [theme.breakpoints.up('xs')]: {
            paddingTop: theme.spacing(4),
        },
    },
    searchFormWrap: {
        marginTop: theme.spacing(2),
        flexGrow: 1,
        [theme.breakpoints.up('sm')]: {
            flexGrow: 0,
        },
    },
    fiterButtonWrap: {
        [theme.breakpoints.up('sm')]: {
            marginRight: theme.spacing(2),
        }
    },
    searchForm: {
        "& .MuiInputBase-input": {
            width: "100%",
        },
    },
    searchTextBox: {
        width: "100%",
    },
    searchIcon: {
        fill: theme.palette.secondary.muted,
    },
    noteListWrap: {
        paddingTop: theme.spacing(2),
        [theme.breakpoints.up('sm')]: {
            paddingTop: theme.spacing(8),
        },
    },
    noteBlock: {
        width: "100%",
        justifyContent: "stretch",
        padding: theme.spacing(2),
        [theme.breakpoints.up('sm')]: {
            padding: theme.spacing(4),
        },
    },
    noteTitle: {
        color: theme.palette.secondary.muted,
    },
    noteSubTitle: {
        color: theme.palette.primary.main,
    },
    noteContent: {
        //color: theme.palette.secondary.muted,
    },
    muted: {
        color: theme.palette.secondary.muted,
    },
}));

export default function NotesPage({ title, backUrl, ...props }) {
    const classes = useStyles();

    const isMobile = useMediaQuery(theme.breakpoints.down('sm'), {
        defaultMatches: true
    });

    const [state, setState] = useState({
        searchQuery: null,
        filters: {
            awards: [],
        },
        notes: [],
        dialogNote: null,
    });

    const awardNameMap = state.notes.reduce((map, note) => {
        map[note.awardId] = note.awardTitle;
        return map;
    }, {});

    const searchNotes = !state.searchQuery 
        ? state.notes
        : state.notes.filter(note => note.entryTitle.toLowerCase().indexOf(state.searchQuery) >= 0);

    const displayNotes = !state.filters.awards.length
        ? searchNotes
        : searchNotes.filter(note => state.filters.awards.indexOf(note.awardId) > -1);
    displayNotes.sort((a, b) => {
        return a.awardTitle.localeCompare(b.awardTitle) 
            || a.categoryTitle.localeCompare(b.categoryTitle) 
            || a.entryTitle.localeCompare(b.entryTitle);
    });
    useEffect(() => {
        getEntryNotes()
            .then(resp => {
                const data = resp.data;
                if(data?.status === "OK") {
                    setState(s => ({
                        ...s,
                        notes: data.notes.map(note => ({
                            ...note,
                        })),
                    }))
                }
            })
        ;
    }, []);

    function handleSearchSubmit(e) {
        e.preventDefault();
        handleSearch(e.currentTarget.searchQuery.value);
    }

    function handleSearchChange(e) {
        handleSearch(e.currentTarget.value);
    }

    function handleSearch(query) {
        setState({
            ...state,
            searchQuery: query.trim().toLowerCase(),
        });
    }

    const [diaglogState, setDialogState] = useState({ open: false });
    const handleClickOpen = (note) => {
        setState({
            ...state,
            dialogNote: note,
        });
        setDialogState({ open: true });
    };

    function handleNotesClose(e, awardId, entryId, content, campaignId = null) {
        setState({
            ...state,
            dialogNoteId: null,
            notes: state.notes.map((note) => {
                if(noteId(note) === makeId(awardId, entryId, campaignId) && content) {
                    note.note = content;
                }
                return note;
            }),
        });
        setDialogState({ open: false });
    }

    function handleDeleteNote(e, entryId) {
        setState({
            ...state,
            notes: state.notes.filter((note) => note.entryId !== entryId),
        });
    }

    const [menuOpen, setMenuOpen] = useState(false);
    const menuButtonRef = useRef(null);
    const notesFilterRef = useRef(null);
    const menuUid = "filtermenu";

    function handleFilterChange(e, item) {
        setState({
            ...state,
            filters: {
                ...state.filters,
                // if the All Notes option selected, set filter to empty array
                awards: item.id === -1 ?
                [] : [
                    item.id,
                ]
            },
        });
        setMenuOpen(false);
        menuButtonRef.current.focus();
    }

    const awardFilterItems = useMemo(() => [
        { id: -1, label: "All Notes"},
        ...(makeUnique(state.notes.map(note => note.awardId))
            .map(awardId => ({
                id: awardId,
                label: awardNameMap[awardId],
            })))
    ], [awardNameMap, state.notes]);

    useScrollToTop();

    return (
        <Page title={title}>
            <Header style={{paddingBottom: "1rem"}} />
            <Toolbar disableGutters={true} className={classes.header}>
                <Container maxWidth="xl">
                    <Grid container className={classes.gridWrap} direction="row" justifyContent="center" alignItems="center">
                        <Grid item xs={2}>
                            <BackLink to={backUrl} style={{marginLeft: "-.5rem"}} />
                        </Grid>
                        <Grid item xs={8} className={classes.titleContainer}>
                            <Typography component="h1" variant="h5" className={classes.title}>{title}</Typography>
                        </Grid>
                        <Grid item xs={2}>
                        </Grid>
                    </Grid>
                </Container>
            </Toolbar>

            <Container maxWidth="xl" className={classes.contentHeaderWrap}>
                <Grid container className={classes.gridWrap} direction="row" justifyContent="space-between">
                    <Grid item xs={12} sm={true}>
                        <Typography variant="h4" component="div" className={"upper "} style={{marginTop: "1.25rem"}}>
                            My Notes
                        </Typography>
                    </Grid>
                    <Grid item className={classes.searchFormWrap}>
                        <Box display="flex" style={{flexWrap: "wrap", justifyContent: "space-between" }}>
                            {displayFilter(state.notes) &&  
                                <Box style={{flexGrow: 1 }} className={classes.fiterButtonWrap}>
                                    <OutlinedButton icon={FilterListIcon} text="Notes filter"
                                        buttonProps={{
                                            ref: menuButtonRef,
                                            style: {
                                                height: "100%",
                                                width: "100%",
                                            },
                                        }}
                                        onClick={() => {
                                            setMenuOpen(!menuOpen);
                                        }}
                                        
                                        aria-controls={menuUid}
                                        aria-haspopup="true" />
                                    <EscapeListener enabled={menuOpen} onKeyPress={() => {
                                        setMenuOpen(false)
                                        if(menuButtonRef.current) 
                                            menuButtonRef.current.focus();
                                    }}>
                                        <TabAwayListener tabRef={notesFilterRef} enabled={menuOpen} tabAwayHandler={() => setMenuOpen(false)}>
                                            <div ref={notesFilterRef}>
                                                <SingleSelectMenu 
                                                    items={awardFilterItems} 
                                                    uid={menuUid} 
                                                    open={menuOpen} 
                                                    onClose={() => setMenuOpen(false)} 
                                                    buttonRef={menuButtonRef} 
                                                    handleChange={handleFilterChange}
                                                />
                                            </div>
                                        </TabAwayListener>
                                    </EscapeListener>
                                </Box>
                            }
                            <form style={{flexGrow: 1 }} className={classes.searchForm} onSubmit={handleSearchSubmit}>
                                <TextField style={{width: "100%"}} placeholder="Search Title" InputProps={{
                                    name: "searchQuery",
                                    onChange: handleSearchChange,
                                    className: classes.searchTextBox,
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon className={classes.searchIcon} />
                                        </InputAdornment>
                                    ),
                                }} />
                            </form>
                        </Box>
                    </Grid>
                </Grid>
            </Container>
            {state.notes.length ?
                <Container maxWidth="md">
                    <div className={[classes.gridWrap, classes.noteListWrap].join(" ")}>
                        <Grid container style={props.styles} spacing={isMobile ? 2 : 8}>
                            { displayNotes.map((note) => (
                                <Grid item xs={12} key={note.id}>
                                    <ButtonBlock className={classes.noteBlock} padding={null} onClick={() => handleClickOpen(note)}>
                                        <Typography variant="overline" component="div" className={classes.noteTitle} style={{marginBottom:".5rem"}}>
                                            {note.awardTitle} {note.categoryTitle ? `(${note.categoryTitle})` : ''}
                                        </Typography>
                                        <Typography variant="subtitle1" className={classes.noteSubTitle} style={{marginBottom:".5rem"}}>
                                            {note.entryTitle}
                                        </Typography>
                                        <Typography variant="body1" className={classes.noteContent} >
                                            <Nl2br text={note.note} />
                                        </Typography>
                                    </ButtonBlock>

                                    {noteId(state.dialogNote) !== noteId(note) ? "" : (
                                        <NotesDialog note={note} diaglogState={diaglogState} setDialogState={setDialogState} handleClose={handleNotesClose} handleDelete={handleDeleteNote} />
                                    )}
                                
                                </Grid>
                            ))}
                        </Grid>
                    </div>
                </Container>
                :
                <Box style={{display: "flex", alignItems: "center", justifyContent: "center", height: '50vh', textAlign: "center", padding: "0 2rem" }}>
                    <Typography variant="h6">
                        The Notes page is where all the notes you have saved for entries are available for you to review. To create notes go to an entry page and click on the Notes button.
                        <br/><br/>
                        Notes will automatically be removed from this page once the viewing period for the award closes.
                    </Typography>
                </Box>
            }
        </Page>
    );
}


function noteId(note) {
    return note && makeId(note.awardId, note.entryId, note.categoryId);
}
function makeId(awardId, entryId, categoryId = null) {
    return `${awardId}-${entryId}${categoryId ? '-' + categoryId : ''}`;
}
function makeUnique(array) {
    return [...new Set(array)];
}

function displayFilter(notes) {
    return makeUnique(notes.map(note => note.awardId)).length > 1
};